import React from 'react';
import Head from 'next/head';
import Layout from '../components/layout';
import Container from '../components/container';
import NavBar from '../components/navbar';
import FeaturedVideoHeroPost from '../components/featured-video-hero-post';
import FeaturedVideoPost from '../components/featured-video-post';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { H2 } from '../components/text';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { IPost } from '../models/post';
import { IPage } from '../models/page';
import { VideoCategorySlider } from '../components/video-category-slider';
import { ICategoryVideo } from '../models/category-video';

import 'swiper/css';

interface IProps {
	resumeVideos: IPost[];
	allCategories: ICategoryVideo[];
	primaryMenuItems: IMenuItem[];
	pageData: IPage;
	sponsors: ISponsor[];
}

export default function VideosPage({
	resumeVideos,
	allCategories,
	primaryMenuItems,
	pageData: { page },
	sponsors,
}: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;
	const [firstVideo, ...restVideos] = resumeVideos;

	return (
		<Layout preview customClass="bg-kkd-dark" sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12">
				<div className="mb-12">
					<H2 className="my-4 text-white">Video</H2>
					<div className="grid grid-cols-12 lg:grid-rows-2 gap-6">
						{firstVideo && (
							<div className="row-span-2 col-span-12 xl:col-span-9">
								<FeaturedVideoHeroPost
									title={firstVideo.node.title}
									coverImage={firstVideo.node.featuredImage}
									slug={firstVideo.node.slug}
									date={firstVideo.node.date}
									baseSlug="video"
								/>
							</div>
						)}

						{restVideos.map((post) => (
							<div className="col-span-6 xl:col-span-3 mb-0" key={post.node.slug}>
								<FeaturedVideoPost
									key={post.node.slug}
									title={post.node.title}
									coverImage={post.node.featuredImage}
									slug={post.node.slug}
									date={post.node.date}
									baseSlug="video"
								/>
							</div>
						))}
					</div>
				</div>

				<div className="flex flex-col gap-12 lg:gap-20">
					{allCategories.map((category) => (
						<VideoCategorySlider
							key={category.name}
							data={category.posts.nodes}
							title={category.name}
							baseSlug="video"
						/>
					))}
				</div>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(`
		query {
			page(id: 108870, idType: DATABASE_ID) {
				title
				content
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`);

	const resumeVideos = await fetchAPI<{ resumeVideos: { edges: any[]; }}>(`
		query getResumeCategory {
			resumeVideos: posts(first: 3, where: { categoryId: 2 }) {
				edges {
					node {
						title
						slug
						date
						featuredImage {
							node {
								sourceUrl
							}
						}
						videoLinks {
							youtubeLink
							fieldGroupName
						}
					}
				}
			}
		}
	`);

	const allCategories = await fetchAPI<{ categories: { nodes: any[]; }}>(`
		query getAllCategories {
			categories(first: 10, where: { parent: 3, order: DESC, orderby: TERM_ID }) {
				nodes {
					name
					posts {
						nodes {
							title
							slug
							date
							featuredImage {
								node {
									sourceUrl
								}
							}
							videoLinks {
								youtubeLink
								fieldGroupName
							}
						}
					}
				}
			}
		}
	`);

	const filteredCategories = allCategories.categories.nodes.filter(
		(category) => category.posts.nodes.length,
	);

	return {
		revalidate: 300,
		props: {
			resumeVideos: resumeVideos.resumeVideos.edges,
			allCategories: filteredCategories,
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
		},
	};
};
